import React, { useState, useEffect } from 'react';
import { Building, Plus, Edit, Trash2, AlertCircle, CheckCircle, Search } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import AdminGlobalLayout from '../../components/AdminGlobalLayout';
import AdminDataTable from '../../components/AdminDataTable';

interface Igreja {
  id: string;
  nome: string;
  cidade: string;
  estado: string;
  pastor_responsavel: string;
  telefone_pastor: string;
  endereco: string;
  cep: string;
  email: string;
  telefone: string;
  website: string;
  created_at: string;
}

interface IgrejaFormData {
  nome: string;
  cidade: string;
  estado: string;
  pastor_responsavel: string;
  telefone_pastor: string;
  endereco: string;
  cep: string;
  email: string;
  telefone: string;
  website: string;
}

export default function AdminIgrejas() {
  const [igrejas, setIgrejas] = useState<Igreja[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editingIgreja, setEditingIgreja] = useState<Igreja | null>(null);
  const [igrejaToDelete, setIgrejaToDelete] = useState<Igreja | null>(null);
  const [formData, setFormData] = useState<IgrejaFormData>({
    nome: '',
    cidade: '',
    estado: '',
    pastor_responsavel: '',
    telefone_pastor: '',
    endereco: '',
    cep: '',
    email: '',
    telefone: '',
    website: ''
  });
  
  // Table state
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sortField, setSortField] = useState<string | null>('nome');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc' | null>('asc');
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredIgrejas, setFilteredIgrejas] = useState<Igreja[]>([]);

  useEffect(() => {
    fetchIgrejas();
  }, []);

  useEffect(() => {
    // Filter and sort igrejas
    let filtered = [...igrejas];
    
    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(igreja => 
        igreja.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
        igreja.cidade.toLowerCase().includes(searchTerm.toLowerCase()) ||
        igreja.estado.toLowerCase().includes(searchTerm.toLowerCase()) ||
        igreja.pastor_responsavel.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Apply sorting
    if (sortField && sortOrder) {
      filtered.sort((a, b) => {
        const aValue = a[sortField as keyof Igreja];
        const bValue = b[sortField as keyof Igreja];
        
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return sortOrder === 'asc' 
            ? aValue.localeCompare(bValue) 
            : bValue.localeCompare(aValue);
        }
        
        return 0;
      });
    }
    
    setFilteredIgrejas(filtered);
  }, [igrejas, searchTerm, sortField, sortOrder]);

  async function fetchIgrejas() {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('igrejas')
        .select('*')
        .order('nome');

      if (error) throw error;
      setIgrejas(data || []);
    } catch (error: any) {
      setError(error.message || 'Erro ao buscar igrejas');
    } finally {
      setLoading(false);
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    try {
      if (editingIgreja) {
        // Update existing igreja
        const { error } = await supabase
          .from('igrejas')
          .update(formData)
          .eq('id', editingIgreja.id);
        
        if (error) throw error;
        setSuccess('Igreja atualizada com sucesso!');
      } else {
        // Create new igreja
        const { error } = await supabase
          .from('igrejas')
          .insert([formData]);
        
        if (error) throw error;
        setSuccess('Igreja criada com sucesso!');
      }
      
      // Reset form and close modal
      setFormData({
        nome: '',
        cidade: '',
        estado: '',
        pastor_responsavel: '',
        telefone_pastor: '',
        endereco: '',
        cep: '',
        email: '',
        telefone: '',
        website: ''
      });
      setIsModalOpen(false);
      setEditingIgreja(null);
      
      // Refresh data
      fetchIgrejas();
    } catch (error: any) {
      setError(error.message || 'Erro ao salvar igreja');
    }
  };

  const handleEdit = (igreja: Igreja) => {
    setEditingIgreja(igreja);
    setFormData({
      nome: igreja.nome,
      cidade: igreja.cidade,
      estado: igreja.estado,
      pastor_responsavel: igreja.pastor_responsavel,
      telefone_pastor: igreja.telefone_pastor,
      endereco: igreja.endereco,
      cep: igreja.cep,
      email: igreja.email,
      telefone: igreja.telefone,
      website: igreja.website
    });
    setIsModalOpen(true);
  };

  const handleDelete = async () => {
    if (!igrejaToDelete) return;
    
    try {
      setError('');
      setSuccess('');
      
      // Check if there are users associated with this igreja
      const { count, error: countError } = await supabase
        .from('users')
        .select('*', { count: 'exact', head: true })
        .eq('igreja_id', igrejaToDelete.id);
      
      if (countError) throw countError;
      
      if (count && count > 0) {
        setError(`Não é possível excluir esta igreja pois existem ${count} usuários vinculados a ela.`);
        setIsDeleteModalOpen(false);
        setIgrejaToDelete(null);
        return;
      }
      
      // Delete igreja
      const { error } = await supabase
        .from('igrejas')
        .delete()
        .eq('id', igrejaToDelete.id);
      
      if (error) throw error;
      
      setSuccess('Igreja excluída com sucesso!');
      setIsDeleteModalOpen(false);
      setIgrejaToDelete(null);
      
      // Refresh data
      fetchIgrejas();
    } catch (error: any) {
      setError(error.message || 'Erro ao excluir igreja');
    }
  };

  const handleSortingChange = (field: string, order: 'asc' | 'desc') => {
    setSortField(field);
    setSortOrder(order);
  };

  const columns = [
    {
      header: 'Nome',
      accessorKey: 'nome',
      enableSorting: true,
      cell: (info: { row: any }) => (
        <div className="font-medium text-gray-900">{info.row.nome}</div>
      )
    },
    {
      header: 'Cidade/Estado',
      accessorKey: 'cidade',
      cell: (info: { row: any }) => (
        <div>{info.row.cidade}/{info.row.estado}</div>
      )
    },
    {
      header: 'Pastor Responsável',
      accessorKey: 'pastor_responsavel',
    },
    {
      header: 'Contato',
      accessorKey: 'telefone',
      cell: (info: { row: any }) => (
        <div>
          <div>{info.row.telefone}</div>
          <div className="text-sm text-gray-500">{info.row.email}</div>
        </div>
      )
    },
    {
      header: 'Ações',
      accessorKey: 'id',
      cell: (info: { row: any }) => (
        <div className="flex justify-end space-x-2">
          <button
            onClick={() => handleEdit(info.row)}
            className="p-1 text-brand-teal hover:text-brand-teal/80 rounded-full"
            title="Editar"
          >
            <Edit className="h-5 w-5" />
          </button>
          <button
            onClick={() => {
              setIgrejaToDelete(info.row);
              setIsDeleteModalOpen(true);
            }}
            className="p-1 text-red-500 hover:text-red-600 rounded-full"
            title="Excluir"
          >
            <Trash2 className="h-5 w-5" />
          </button>
        </div>
      )
    }
  ];

  // Calculate pagination
  const pageCount = Math.ceil(filteredIgrejas.length / pageSize);
  const paginatedData = filteredIgrejas.slice(
    pageIndex * pageSize,
    (pageIndex + 1) * pageSize
  );

  return (
    <AdminGlobalLayout title="Gerenciar Igrejas">
      {error && (
        <div className="mb-6 p-4 bg-red-50 text-red-600 rounded-lg flex items-start">
          <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {success && (
        <div className="mb-6 p-4 bg-green-50 text-green-600 rounded-lg flex items-start">
          <CheckCircle className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
          <span>{success}</span>
        </div>
      )}

      <AdminDataTable
        data={paginatedData}
        columns={columns}
        title="Igrejas"
        subtitle={`Total de ${filteredIgrejas.length} igrejas cadastradas`}
        loading={loading}
        pagination={{
          pageIndex,
          pageSize,
          pageCount,
          onPageChange: setPageIndex
        }}
        sorting={{
          field: sortField,
          order: sortOrder,
          onSortingChange: handleSortingChange
        }}
        filtering={{
          globalFilter: searchTerm,
          onGlobalFilterChange: setSearchTerm
        }}
        enableExport={true}
        exportFileName="igrejas"
        actions={
          <button
            onClick={() => {
              setEditingIgreja(null);
              setFormData({
                nome: '',
                cidade: '',
                estado: '',
                pastor_responsavel: '',
                telefone_pastor: '',
                endereco: '',
                cep: '',
                email: '',
                telefone: '',
                website: ''
              });
              setIsModalOpen(true);
            }}
            className="px-3 py-2 bg-brand-teal text-white rounded-lg hover:bg-brand-teal/90 transition-colors flex items-center gap-2 text-sm"
          >
            <Plus className="h-4 w-4" />
            <span>Nova Igreja</span>
          </button>
        }
      />

      {/* Igreja Form Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold text-brand-teal mb-6">
              {editingIgreja ? 'Editar Igreja' : 'Nova Igreja'}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label htmlFor="nome" className="block text-sm font-medium text-gray-700 mb-1">
                    Nome da Igreja *
                  </label>
                  <input
                    type="text"
                    id="nome"
                    name="nome"
                    value={formData.nome}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal"
                  />
                </div>
                
                <div>
                  <label htmlFor="cidade" className="block text-sm font-medium text-gray-700 mb-1">
                    Cidade *
                  </label>
                  <input
                    type="text"
                    id="cidade"
                    name="cidade"
                    value={formData.cidade}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal"
                  />
                </div>
                
                <div>
                  <label htmlFor="estado" className="block text-sm font-medium text-gray-700 mb-1">
                    Estado *
                  </label>
                  <select
                    id="estado"
                    name="estado"
                    value={formData.estado}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal"
                  >
                    <option value="">Selecione...</option>
                    <option value="AC">Acre</option>
                    <option value="AL">Alagoas</option>
                    <option value="AP">Amapá</option>
                    <option value="AM">Amazonas</option>
                    <option value="BA">Bahia</option>
                    <option value="CE">Ceará</option>
                    <option value="DF">Distrito Federal</option>
                    <option value="ES">Espírito Santo</option>
                    <option value="GO">Goiás</option>
                    <option value="MA">Maranhão</option>
                    <option value="MT">Mato Grosso</option>
                    <option value="MS">Mato Grosso do Sul</option>
                    <option value="MG">Minas Gerais</option>
                    <option value="PA">Pará</option>
                    <option value="PB">Paraíba</option>
                    <option value="PR">Paraná</option>
                    <option value="PE">Pernambuco</option>
                    <option value="PI">Piauí</option>
                    <option value="RJ">Rio de Janeiro</option>
                    <option value="RN">Rio Grande do Norte</option>
                    <option value="RS">Rio Grande do Sul</option>
                    <option value="RO">Rondônia</option>
                    <option value="RR">Roraima</option>
                    <option value="SC">Santa Catarina</option>
                    <option value="SP">São Paulo</option>
                    <option value="SE">Sergipe</option>
                    <option value="TO">Tocantins</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="pastor_responsavel" className="block text-sm font-medium text-gray-700 mb-1">
                    Pastor Responsável *
                  </label>
                  <input
                    type="text"
                    id="pastor_responsavel"
                    name="pastor_responsavel"
                    value={formData.pastor_responsavel}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal"
                  />
                </div>
                
                <div>
                  <label htmlFor="telefone_pastor" className="block text-sm font-medium text-gray-700 mb-1">
                    Telefone do Pastor
                  </label>
                  <input
                    type="tel"
                    id="telefone_pastor"
                    name="telefone_pastor"
                    value={formData.telefone_pastor}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal"
                  />
                </div>
                
                <div className="md:col-span-2">
                  <label htmlFor="endereco" className="block text-sm font-medium text-gray-700 mb-1">
                    Endereço *
                  </label>
                  <input
                    type="text"
                    id="endereco"
                    name="endereco"
                    value={formData.endereco}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal"
                  />
                </div>
                
                <div>
                  <label htmlFor="cep" className="block text-sm font-medium text-gray-700 mb-1">
                    CEP
                  </label>
                  <input
                    type="text"
                    id="cep"
                    name="cep"
                    value={formData.cep}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal"
                  />
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal"
                  />
                </div>
                
                <div>
                  <label htmlFor="telefone" className="block text-sm font-medium text-gray-700 mb-1">
                    Telefone da Igreja *
                  </label>
                  <input
                    type="tel"
                    id="telefone"
                    name="telefone"
                    value={formData.telefone}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal"
                  />
                </div>
                
                <div>
                  <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-1">
                    Website
                  </label>
                  <input
                    type="url"
                    id="website"
                    name="website"
                    value={formData.website}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-teal focus:border-brand-teal"
                  />
                </div>
              </div>
              
              <div className="flex justify-end gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-brand-teal text-white rounded-lg hover:bg-brand-teal/90 transition-colors"
                >
                  {editingIgreja ? 'Atualizar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && igrejaToDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Confirmar exclusão</h3>
            <p className="text-gray-600 mb-6">
              Tem certeza que deseja excluir a igreja <strong>{igrejaToDelete.nome}</strong>? Esta ação não pode ser desfeita.
            </p>
            <div className="flex justify-end gap-4">
              <button
                onClick={() => {
                  setIsDeleteModalOpen(false);
                  setIgrejaToDelete(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminGlobalLayout>
  );
}